import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { assert, isDefined } from '@sama/js-core';
import { identity, isEmpty, isEqual, some } from '@sama/micro-dash';
import { difference, flattenDeep, values } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, skip, switchMapTo, take, takeUntil } from 'rxjs/operators';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { AbstractCallbackService } from '../../../shared/misc-services/abstract-callback.service';
import { DraftSubmissionResource } from '../../../shared/resources/draft-submission-resource.service';
import { ExecutionQueueService } from '../../../shared/save-manager/execution-queue.service';
import { PeriodicSaveManagerService } from '../../../shared/save-manager/periodic-save-manager.service';
import {
  Answer,
  AnswersByName,
} from '../../../team-apis/otterdam/answers/answer';
import { ShapeGroupExternal } from '../../../team-apis/otterdam/answers/shape-typings';
import { DraftSubmission } from '../../../team-apis/otterdam/draft-submission';
import { Task } from '../../../team-apis/otterdam/task';
import { areDraftSavesEligible } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { FeatureFlagService } from '../../shared/misc-services/feature-flag.service';
import { AutoSaveFailModalComponent } from '../auto-save-fail-modal/auto-save-fail-modal.component';
import { TimerService } from '../timer-badge/timer.service';

@Injectable()
export class DraftSubmissionService extends AbstractCallbackService {
  isSaving$ = new Subject<boolean>();
  consecutiveFailureCount$ = new BehaviorSubject(0);
  private savedAnswers?: AnswersByName;
  private stepId?: number;
  private featureFlagService = inject(FeatureFlagService);
  private isInitialSave = true;

  constructor(
    private draftSubmissionResource: DraftSubmissionResource,
    private periodicSaveManager: PeriodicSaveManagerService,
    private store: TaskWorkspaceStore,
    private timerService: TimerService,
    private fusionManagerFactory: FusionManagerFactory,
    private dialogService: MatDialog,
    private executionQueue: ExecutionQueueService,
    private tokenService: TokenManagementService,
    injector: Injector,
  ) {
    super(injector);
  }

  private get currentTask(): Task {
    return this.store.state().task;
  }

  initialize(stepId: number) {
    this.stepId = stepId;
    this.periodicSaveManager.initialize(async () => {
      const token = await this.tokenService.getToken('access_token');
      await this.saveDraft(false, token);
    });

    this.subscribeTo(
      this.store.layoutComplete$.pipe(
        switchMapTo(this.store('task')('id').$),
        switchMapTo(
          this.store('outputInfoByName').$.pipe(
            filter(() => this.canSaveDrafts()),
            take(1),
          ),
        ),
      ),
      () => {
        this.resetSavedAnswers();
      },
    );
  }

  saveDraft(saveRegardlessOfAnswers = false, token: string) {
    if (!this.canSaveDrafts()) {
      return undefined;
    }

    const result = this.runCallbacks();
    if (result) {
      return result.then((callbackResults) =>
        this.checkAnswersAndSave(
          saveRegardlessOfAnswers || some(callbackResults, identity),
          token,
        ),
      );
    }
    return this.checkAnswersAndSave(saveRegardlessOfAnswers, token);
  }

  haveAnswersChanged() {
    const currentAnswers = this.getCurrentAnswers();
    const hasChanged = !isEqual(currentAnswers, this.savedAnswers);
    if (
      hasChanged &&
      this.featureFlagService.hasFlag('log-draft-saves_on') &&
      this.isInitialSave
    ) {
      const shapeIdsFromInitialLoad = getAllShapeIndexesFromAnswer(
        this.savedAnswers,
      );
      const currentShapeIds = getAllShapeIndexesFromAnswer(currentAnswers);
      if (difference(shapeIdsFromInitialLoad, currentShapeIds).length > 0) {
        this.rollbarService.log(
          'Initial Draft save triggered',
          currentAnswers,
          this.savedAnswers,
          difference(shapeIdsFromInitialLoad, currentShapeIds),
        );
        this.isInitialSave = false;
      }
    }
    return hasChanged;
  }

  resetSavedAnswers() {
    this.savedAnswers = this.getCurrentAnswers();
  }

  private canSaveDrafts() {
    const state = this.store.state();
    return (
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      state?.outputInfoByName && areDraftSavesEligible(state.outputInfoByName)
    );
  }

  private async checkAnswersAndSave(
    saveRegardlessOfAnswers: boolean,
    token: string,
  ) {
    this.validateAnswer();
    const currentAnswers = this.getCurrentAnswers();
    if (!saveRegardlessOfAnswers && !this.haveAnswersChanged()) {
      return;
    }
    this.isSaving$.next(true);
    try {
      await this.sendSaveRequest(currentAnswers, token);
    } catch (error: unknown) {
      this.handleSaveError(saveRegardlessOfAnswers, token);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  private handleSaveError(saveRegardlessOfAnswers: boolean, token: string) {
    this.consecutiveFailureCount$.next(this.consecutiveFailureCount$.value + 1);

    if (this.consecutiveFailureCount$.value === 11) {
      const ref = this.dialogService.open(AutoSaveFailModalComponent, {
        disableClose: true,
      });
      ref.componentInstance.retryFn = () =>
        this.executionQueue.enqueue(() =>
          this.checkAnswersAndSave(saveRegardlessOfAnswers, token),
        );
      this.subscribeTo(
        this.store('lastSavedDraftTimestamp').$.pipe(
          skip(1),
          takeUntil(ref.afterClosed()),
        ),
        () => ref.close(),
      );
    }
  }

  private async sendSaveRequest(answers: AnswersByName, token: string) {
    const draft = this.createSubmission(answers);
    await this.draftSubmissionResource.create_or_update(draft, token);
    this.savedAnswers = answers;
    this.consecutiveFailureCount$.next(0);
    this.store('lastSavedDraftTimestamp').set(Date.now());
  }

  private createSubmission(currentAnswers: AnswersByName): DraftSubmission {
    const task = this.currentTask;
    const lastInteractiveEventTimestamp =
      this.store.state().lastInteractiveEventTimestamp;

    return {
      step_id: this.stepId,
      task_id: task.id,
      value: JSON.stringify(currentAnswers),
      next_internal_id: task.next_internal_id,
      duration: this.timerService.elapsed(lastInteractiveEventTimestamp),
    } as DraftSubmission;
  }

  private validateAnswer() {
    const answers = this.store.extractAnswersFromOutputInfo();
    const pointCloud = this.store
      .state()
      .project.outputs.find((output) => output.type === 'PointCloudOutput');
    if (!pointCloud) {
      return;
    }
    const errors = this.fusionManagerFactory
      .getManager(pointCloud.id)
      .validateAnswer(answers, pointCloud.name);

    assert(
      isEmpty(errors),
      'Unexpected error during draft save: ' + errors.join('; '),
    );
  }

  private getCurrentAnswers(): AnswersByName {
    return this.store.extractAnswersFromOutputInfo();
  }
}

/** @deprecated used for old projects without objects */
function getAllShapeIndexesFromAnswer(answers: AnswersByName = {}) {
  return flattenDeep(
    values(answers).map((answer: Answer) =>
      answer.shape_groups?.map((shapeGroup: ShapeGroupExternal<any, any>) =>
        shapeGroup.shapes.map((shape) => shape.index),
      ),
    ),
  ).filter(isDefined);
}
